import { useLargeFromMediaQuery } from '@madpaws/design-system';
import { Field, useFormikContext } from 'formik';
import React, { useContext } from 'react';

import {
  SET_WEEKLY_AS_THE_DEFAULT_OPTION_FOR_RWB_EPPO_ID,
  SET_WEEKLY_AS_THE_DEFAULT_OPTION_FOR_RWB_EPPO_ID_VALUES,
} from '~/components/Eppo/constants';
import { useEppoStringAssignment } from '~/components/Eppo/helpers';
import { BOOKING_TYPE_MARKETPLACE, BOOKING_TYPE_RWB } from '~/components/utils/search';

import { ServiceTypeSelector } from './ServiceTypeSelector';
import { RWB_SERVICE_NAMES, SEARCH_FILTERS_FORM_KEYS as KEYS } from '../../constants';
import { DialogContext } from '../DialogFormField/DialogFormField';

import type { RadioGroupOnChange } from '@madpaws/design-system';
import type { FieldProps } from 'formik';
import type { ReactElement } from 'react';
import type { SearchFilters } from '~/common/types/search';
import type { ServiceOption } from '~/components/constants';

type Props = {
  isBookingTypeChangeEnabled?: boolean;
  options?: ServiceOption[];
};

export const ServiceTypeField = ({
  options,
  isBookingTypeChangeEnabled = false,
}: Props): ReactElement => {
  const { setFieldValue } = useFormikContext<SearchFilters>();
  const { handleDialogClose } = useContext(DialogContext);
  const isLargeFromViewport = useLargeFromMediaQuery();

  const isSetWeeklyAsTheDefaultOptionForRWB =
    useEppoStringAssignment(SET_WEEKLY_AS_THE_DEFAULT_OPTION_FOR_RWB_EPPO_ID) ===
    SET_WEEKLY_AS_THE_DEFAULT_OPTION_FOR_RWB_EPPO_ID_VALUES.ON;

  const handleChange = (event: RadioGroupOnChange): void => {
    const {
      target: {
        value: { value: fieldValue },
      },
    } = event;
    setFieldValue(KEYS.serviceType, fieldValue);

    if (isSetWeeklyAsTheDefaultOptionForRWB && isBookingTypeChangeEnabled) {
      setFieldValue(
        KEYS.bookingType,
        RWB_SERVICE_NAMES.includes(fieldValue) ? BOOKING_TYPE_RWB : BOOKING_TYPE_MARKETPLACE
      );
    }

    if (!isLargeFromViewport) {
      handleDialogClose?.();
    }
  };

  return (
    <Field name={KEYS.serviceType}>
      {({ field }: FieldProps): ReactElement => {
        const { name, value } = field;
        return (
          <ServiceTypeSelector
            isLegendVisuallyHidden={!isLargeFromViewport}
            legend="What service are you looking for?"
            name={name}
            onChange={handleChange}
            options={options}
            value={value}
          />
        );
      }}
    </Field>
  );
};
